import { FC, useMemo } from 'react'

import { useWallet } from '@solana/wallet-adapter-react'
import classNames from 'classnames'
import {
  calculateAdventureRewards,
  getHistoryAdventureStakeUserInfo,
} from 'fbonds-core/lib/fbond-protocol/functions/banxStaking/banxTokenStaking'

import { staking } from '@banx/api/common'
import { BanxLogo, Info } from '@banx/icons'
import { ZERO_BN, formatCompact, formatNumbersWithCommas, formatTrailingZeros } from '@banx/utils'

import { banxTokenBNToFixed, isAdventureEnded } from '../../helpers'
import { AdventureCardHeader, TotalParticipationColumn } from './components'

import styles from './AdventuresContent.module.less'

interface AdventuresListProps {
  banxStakeInfo: staking.BanxStakingInfo
  className?: string
}

export const HistoryAdventuresList: FC<AdventuresListProps> = ({ banxStakeInfo, className }) => {
  const { adventures, subscription, subscriptionSnapshots } = banxStakeInfo

  const endedAdventures = useMemo(
    () => adventures.filter(isAdventureEnded).sort((a, b) => b.week.sub(a.week).toNumber()),
    [adventures],
  )

  return (
    <ul className={classNames(styles.list, className)}>
      {endedAdventures.map((adventure) => (
        <AdventuresCard
          key={adventure.publicKey.toBase58()}
          adventure={adventure}
          snapshots={subscriptionSnapshots}
          subscription={subscription ?? undefined}
        />
      ))}
    </ul>
  )
}

interface AdventuresCardProps {
  adventure: staking.BanxAdventure
  snapshots: staking.BanxAdventureSnapshot[]
  subscription?: staking.BanxAdventureSubscription
}

const AdventuresCard: FC<AdventuresCardProps> = ({ adventure, snapshots, subscription }) => {
  const { connected } = useWallet()

  const snapshot = useMemo(
    () => snapshots.find((s) => s.week.eq(adventure.week)),
    [adventure, snapshots],
  )

  return (
    <li className={styles.card}>
      <AdventureCardHeader adventure={adventure} />

      <div className={styles.info}>
        <AdventureEndedRewardsResult adventure={adventure} snapshot={snapshot} />

        <div className={styles.stats}>
          <TotalParticipationColumn adventure={adventure} />

          {connected && snapshot && (
            <WalletParticipationColumn adventure={adventure} snapshot={snapshot} />
          )}

          {connected && !snapshot && (
            <NotParticipatedColumn adventure={adventure} subscription={subscription} />
          )}
        </div>
      </div>
    </li>
  )
}

type WalletParticipationColumnProps = {
  adventure: staking.BanxAdventure
  snapshot: staking.BanxAdventureSnapshot
}

export const WalletParticipationColumn: FC<WalletParticipationColumnProps> = ({
  adventure,
  snapshot,
}) => {
  const adventureStats = getHistoryAdventureStakeUserInfo({ adventure, snapshot })
  const { nftsStaked, tokensStaked, partnerPointsStaked } = adventureStats

  //? Formatting values
  const formattedNftsStaked = formatNumbersWithCommas(nftsStaked.toString())
  const formattedTokensStaked = formatCompact(banxTokenBNToFixed(tokensStaked))
  const formattedPartnerPoints = formatTrailingZeros(
    formatNumbersWithCommas(partnerPointsStaked.toString()),
  )

  return (
    <div className={styles.statsCol}>
      <h5>You participated with</h5>
      <p>{formattedNftsStaked} Banx</p>
      <p>{formattedTokensStaked} $BANX</p>
      <p>{formattedPartnerPoints} Partner pts</p>
    </div>
  )
}

type NotParticipatedColumnProps = {
  adventure: staking.BanxAdventure
  subscription?: staking.BanxAdventureSubscription
}

export const NotParticipatedColumn: FC<NotParticipatedColumnProps> = ({
  adventure,
  subscription,
}) => {
  const claimableRewards = subscription
    ? calculateAdventureRewards({
        adventure: [adventure],
        subscription,
      })
    : ZERO_BN

  const title = claimableRewards.gt(ZERO_BN)
    ? 'Claim your rewards to see your history'
    : 'You missed this adventure'

  return (
    <div className={styles.statsColWarn}>
      <Info />
      <p>{title}</p>
    </div>
  )
}

type AdventureEndedRewardsResultProps = {
  adventure: staking.BanxAdventure
  snapshot?: staking.BanxAdventureSnapshot
}

export const AdventureEndedRewardsResult: FC<AdventureEndedRewardsResultProps> = ({
  adventure,
  snapshot,
}) => {
  const { connected } = useWallet()

  const rewards: string = useMemo(() => {
    if (!connected) {
      return banxTokenBNToFixed(adventure.amountOfTokensHarvested, 0)
    }

    if (!snapshot) {
      return '0'
    }

    return banxTokenBNToFixed(
      getHistoryAdventureStakeUserInfo({ adventure, snapshot }).rewardsClaimed,
      0,
    )
  }, [connected, adventure, snapshot])

  const title = connected ? 'You received' : 'Total distributed'

  return (
    <div className={styles.endedRewards}>
      <div className={styles.endedRewardsValue}>
        <p>{formatNumbersWithCommas(rewards)}</p>
        <BanxLogo className={styles.endedRewardsBanxLogo} />
      </div>
      <p className={styles.endedRewardsText}>{title}</p>
    </div>
  )
}
